import type { ExpressionSpecification, Map, SymbolLayerSpecification } from "@iventis/mapbox-gl";
import type { BehaviorSubject } from "rxjs";
import { ModelStyle } from "@iventis/domain-model/model/modelStyle";
import { DataField } from "@iventis/domain-model/model/dataField";
import { StyleValue } from "@iventis/domain-model/model/styleValue";
import { StyleValueExtractionMethod } from "@iventis/domain-model/model/styleValueExtractionMethod";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import { modelTextSuffix } from "../../bridge/constants";
import type { MapModuleLayer, MapState } from "../../types/store-schema";
import type { StylePropertyToValueMapV2 } from "../../types/style-types";
import { styleValueToMapboxStyleValue } from "../mapbox/engine-mapbox-helpers";
import { ApplyListItemTextFunction } from "../mapbox/engine-mapbox-types-and-constants";
import { removeLayerFilterByType, updateLayerFilters } from "../mapbox/filters/engine-mapbox-filter-helpers";
import { FilterType } from "../mapbox/filters/engine-mapbox-filter-types";
import { DataFieldListItemStore } from "../../data-store/data-field-list-item-store";
import { MapboxTextLayer, TextLayerSource } from "./mapbox-text-layer";

/**
 * Handles the integration between a `MapboxModelLayer` and a `MapboxTextLayer`. Positions the
 * text above the 3D model by supplying model-specific paint/layout expressions to the text
 * layer and augments the source features with the map object's stand height so the text
 * sits at the correct elevation.
 */
export class MapboxModelTextLayer extends MapboxTextLayer {
    // Constants
    private static readonly MODEL_TEXT_HEIGHT_PADDING = 0.25;

    private static readonly MODEL_TEXT_STAND_HEIGHT_PROPERTY = "_iventisModelTextStandHeight";

    private readonly hiddenObjectIds = new Set<string>();

    private readonly modelLayerId: string;

    private readonly initialLayer: MapModuleLayer;

    private initialised = false;

    constructor(
        map: Map,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        private readonly altitudeDataField: DataField,
        private readonly modelDataFieldListItemStore: DataFieldListItemStore,
        events: {
            getAboveLayerId: (layerId: string) => string;
            applyTextFieldExpression?: ApplyListItemTextFunction;
        },
        private readonly preview: boolean,
        layerScale?: number
    ) {
        super(
            map,
            layer,
            state$,
            modelDataFieldListItemStore,
            {
                getAboveLayerId: events.getAboveLayerId,
                applyTextFieldExpression: events.applyTextFieldExpression,
            },
            { layerIdSuffix: modelTextSuffix, metadataType: "model-text" },
            layerScale
        );
        this.modelLayerId = layer.id;
        this.initialLayer = layer;
    }

    public addLayer(): void {
        super.addLayer();
        this.initialised = true;

        const features = this.getBaseGeoJson();
        if (features != null) {
            this.updateLocalSource(features);
        }
        this.loadMappedPropertyHeightListItems();
        this.updateFilter();
    }

    public updateModelStyle(styleChanges: StylePropertyToValueMapV2<Pick<ModelStyle, "height" | "modelAltitude">>): void {
        if (styleChanges.modelAltitude || styleChanges.height) {
            this.updateTextLayerElevation();
        }
        if (styleChanges.height) {
            this.loadMappedPropertyHeightListItems();
        }
    }

    public updateLocalSource(source: TextLayerSource): void {
        super.updateLocalSource(this.createTextLocalSource(source));
    }

    public onDrawingStarted(objectIds: string[]): void {
        super.onDrawingStarted(objectIds);
        objectIds.forEach((objectId) => this.hiddenObjectIds.add(objectId));
        this.updateFilter();
    }

    public onDrawingFinished(objectIds: string[]): void {
        super.onDrawingFinished(objectIds);
        objectIds.forEach((objectId) => this.hiddenObjectIds.delete(objectId));
        this.updateFilter();
    }

    public updateSelectionFilter(): void {
        this.updateFilter();
    }

    private getModelLayer(): MapModuleLayer {
        return this.state$.value.layers.value.find((layer) => layer.id === this.modelLayerId) ?? this.initialLayer;
    }

    private getBaseGeoJson(): TextLayerSource | null {
        return this.state$.value.geoJSON.value[this.modelLayerId]?.map(({ feature }) => feature) as TextLayerSource;
    }

    private createTextLocalSource(source: TextLayerSource): TextLayerSource {
        if (this.preview) {
            return source;
        }

        const layerAltitude = getStaticStyleValue(this.getModelLayer().modelStyle.modelAltitude) ?? 0;
        const selectedObjectIds = new Set(this.state$.value.mapObjectsSelected.value.filter(({ layerId }) => layerId === this.modelLayerId).map(({ objectId }) => objectId));

        return source.map((feature) => {
            // When an object is selected, we want to apply the stand height to the offset, otherwise it will jump on select/deselect due to the height change
            const objectAltitude = (feature.properties[this.altitudeDataField.id] as number) ?? 0;
            const standHeight = selectedObjectIds.has(feature.properties.id) ? objectAltitude + layerAltitude : 0;
            return {
                ...feature,
                properties: {
                    ...feature.properties,
                    [MapboxModelTextLayer.MODEL_TEXT_STAND_HEIGHT_PROPERTY]: standHeight,
                },
            };
        });
    }

    private updateTextLayerElevation() {
        this.setPaintProperty("symbol-z-offset", this.createModelTextElevationExpression(this.getModelLayer()));
    }

    /**
     * Builds the `symbol-z-offset` expression for model text.
     */
    private createModelTextElevationExpression(iventisLayer: MapModuleLayer): number | ExpressionSpecification {
        const { height } = iventisLayer.modelStyle;

        if (height == null) {
            return 0;
        }

        return [
            "-",
            ["+", this.createModelHeightExpression(height), this.createTextModelAltitudeExpression(iventisLayer), MapboxModelTextLayer.MODEL_TEXT_HEIGHT_PADDING],
            this.createModelStandHeightExpression(),
        ];
    }

    /**
     * Returns an expression that reads the per-feature stand height adjustment.
     */
    private createModelStandHeightExpression(): number | ExpressionSpecification {
        if (this.preview) {
            // In preview mode, there's no stands, so we can just return 0.
            return 0;
        }

        return ["coalesce", ["get", MapboxModelTextLayer.MODEL_TEXT_STAND_HEIGHT_PROPERTY], 0];
    }

    /**
     * Builds the model height expression from the configured style value.
     */
    private createModelHeightExpression(height: StyleValue<number>): number | ExpressionSpecification {
        if (height.extractionMethod === StyleValueExtractionMethod.MappedProperty) {
            // If the height is mapped, we need to build a case expression that maps the list item IDs to their corresponding height values.
            const expression: ExpressionSpecification = ["case"];
            const listItems = this.modelDataFieldListItemStore.getCachedDataFieldListItems(height.dataFieldId) ?? [];
            for (const listItemId of height.mappedPropertyListItemIds) {
                const listItem = listItems.find((listItem) => listItem.id === listItemId);
                const propertyValue = listItem?.propertyValues.find((propertyValue) => propertyValue.propertyId === height.dataFieldListItemPropertyId)?.number;
                expression.push(["==", ["get", height.dataFieldId], listItemId], propertyValue ?? height.staticValue.staticValue);
            }
            expression.push(height.staticValue.staticValue);
            return expression;
        }

        return styleValueToMapboxStyleValue(height) as number | ExpressionSpecification;
    }

    private async loadMappedPropertyHeightListItems(): Promise<void> {
        const modelLayer = this.getModelLayer();
        const { height } = modelLayer.modelStyle;

        if (height?.extractionMethod !== StyleValueExtractionMethod.MappedProperty) {
            return;
        }

        await this.modelDataFieldListItemStore.getDataFieldListItems(height.dataFieldId);
        this.updateTextLayerElevation();
    }

    private getSelectedObjectIds(): string[] {
        return this.state$.value.mapObjectsSelected.value.filter((object) => object.layerId === this.modelLayerId).map((object) => object.objectId);
    }

    private updateFilter() {
        if (!this.initialised) {
            return;
        }

        const currentFilter = this.map.getFilter(this.layerId);
        const hiddenObjectIds = new Set(Array.from(this.hiddenObjectIds).concat(this.getSelectedObjectIds()));
        const hiddenObjectFilters = Array.from(hiddenObjectIds).map((objectId) => ["!=", ["get", "id"], objectId]);
        const updatedFilter =
            hiddenObjectFilters.length > 0 ? updateLayerFilters(currentFilter, ...hiddenObjectFilters) : removeLayerFilterByType(currentFilter, FilterType.MapObject);
        this.map.setFilter(this.layerId, updatedFilter);
    }

    /**
     * Builds the altitude expression combining layer altitude and the map object datafield altitude
     */
    private createTextModelAltitudeExpression(iventisLayer: MapModuleLayer): number | ExpressionSpecification {
        const layerAltitude = getStaticStyleValue(iventisLayer.modelStyle.modelAltitude) ?? 0;
        // In previews, there won't be any attribute to get altitude from
        if (this.preview) {
            return layerAltitude;
        }

        return ["+", ["coalesce", ["get", this.altitudeDataField.id], 0], layerAltitude];
    }

    protected iventisLayerToMapboxTextLayer(iventisLayer: MapModuleLayer): SymbolLayerSpecification {
        const style = super.iventisLayerToMapboxTextLayer(iventisLayer);
        return {
            ...style,
            paint: {
                ...style.paint,
                "symbol-z-offset": this.createModelTextElevationExpression(iventisLayer),
            },
            layout: {
                ...style.layout,
                "symbol-z-elevate": iventisLayer.modelStyle.height != null,
                "symbol-elevation-reference": "ground",
                "text-anchor": "bottom",
            },
        };
    }
}
